import { createClient } from '@supabase/supabase-js';

// Initialize the Supabase client with environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if environment variables are properly set
if (!supabaseUrl || !supabaseAnonKey) {
  console.error('Supabase URL or Anon Key is missing from environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true
  },
  global: {
    headers: {
      'Accept': '*/*',
      'Content-Type': 'application/json',
      'Prefer': 'return=representation'
    }
  },
  db: {
    schema: 'public'
  },
  realtime: {
    timeout: 20000 // Timeout after 20 seconds
  }
});

// For debugging
console.log('Supabase client initialized with URL:', supabaseUrl);

// Helper function to execute a Supabase request with retry
export const executeWithRetry = async (operation, maxRetries = 2, delayMs = 1000) => {
  let lastError;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await operation();
    } catch (error) {
      console.warn(`Operation failed (attempt ${attempt + 1}/${maxRetries + 1}):`, error);
      lastError = error;
      
      // Don't wait on the last attempt
      if (attempt < maxRetries) {
        // Wait with exponential backoff
        await new Promise(resolve => setTimeout(resolve, delayMs * Math.pow(2, attempt)));
      }
    }
  }
  throw lastError;
};

// Helper function to get current user
export const getCurrentUser = async () => {
  try {
    const { data, error } = await executeWithRetry(() => supabase.auth.getUser());
    if (error) throw error;
    return data.user;
  } catch (error) {
    console.error('Error getting current user:', error.message);
    return null;
  }
};

// Helper function to get current session
export const getCurrentSession = async () => {
  try {
    const { data, error } = await supabase.auth.getSession();
    if (error) throw error;
    return data.session;
  } catch (error) {
    console.error('Error getting current session:', error.message);
    return null;
  }
};

// Helper function to create a user profile if it doesn't exist
export const ensureUserProfile = async (userId, userData = {}) => {
  if (!userId) {
    console.error('Cannot create user profile: No user ID provided');
    return null;
  }
  
  try {
    // Check if profile exists
    const { data: existingProfile, error: profileError } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();
      
    if (profileError) {
      // PGRST116 is the error code for "no rows returned"
      if (profileError.code !== 'PGRST116') {
        console.error('Error checking for existing profile:', profileError);
        throw profileError;
      }
    }
    
    // If profile exists, return it
    if (existingProfile) {
      return existingProfile;
    }
    
    // If profile doesn't exist, create it
    const { data, error } = await supabase
      .from('user_profiles')
      .insert([
        { 
          id: userId,
          full_name: userData.full_name || '',
          ...userData
        }
      ])
      .select();
      
    if (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
    
    console.log('User profile created successfully');
    return data[0];
  } catch (error) {
    console.error('Error in user profile creation process:', error);
    throw error;
  }
};

// Helper function to update user profile
export const updateUserProfile = async (userId, userData) => {
  if (!userId) {
    console.error('Cannot update user profile: No user ID provided');
    return null;
  }
  
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .update(userData)
      .eq('id', userId)
      .select();
      
    if (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
    
    return data[0];
  } catch (error) {
    console.error('Error in user profile update process:', error);
    throw error;
  }
};

// Add a custom fetch function with explicit headers to handle cart operations
export const fetchWithExplicitHeaders = async (endpoint, options = {}) => {
  const url = `${supabaseUrl}${endpoint}`;
  const headers = {
    'Accept': '*/*',
    'apikey': supabaseAnonKey,
    'Content-Type': 'application/json',
    'Prefer': 'return=representation',
    ...options.headers
  };
  
  try {
    // Add timeout to fetch
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), options.timeout || 10000);
    
    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal
    });
    
    // Clear timeout
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => 'No error details available');
      throw new Error(`API request failed with status ${response.status}: ${response.statusText}. Details: ${errorText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error in fetchWithExplicitHeaders:', error);
    
    // Special handling for timeout errors
    if (error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    
    throw error;
  }
};